
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";

export async function processWaitlistSubmission(userId: string, formData: any) {
  // Check if user already has a waitlist submission
  const { data: existingSubmission } = await supabase
    .from('waitlist_submissions')
    .select('id')
    .eq('user_id', userId)
    .limit(1);
    
  if (!existingSubmission?.length) {
    // Create new waitlist submission
    await supabase.from('waitlist_submissions').insert({
      user_id: userId,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      preferred_location: formData.preferredLocation || [],
      specific_nursing_home: formData.specificNursingHome,
      care_needs: formData.careNeeds || [],
      care_needs_details: formData.careNeedsDetails,
      mobility_requirements: formData.mobilityRequirements,
      resident_relation: formData.residentRelation,
      resident_salutation: formData.residentSalutation,
      resident_last_name: formData.residentLastName,
      resident_age: formData.residentAge,
      timeframe: formData.timeframe,
      fair_deal_scheme: formData.fairDealScheme,
      contact_preference: formData.contactPreference,
      additional_information: formData.additionalInformation,
    });
  }
}

export async function processNursingHomeLeads(
  userId: string, 
  formData: any, 
  nursingHomeName?: string | null, 
  nursingHomeSlug?: string | null, 
  countyName?: string | null
): Promise<string[]> {
  // Track which homes we've already added to prevent duplicates
  const addedHomes = new Set<string>();
  const addedHomeNames: string[] = [];
  
  // Add specific nursing home from URL parameters if provided
  if (nursingHomeName && nursingHomeSlug) {
    await addNursingHomeLead(
      userId,
      nursingHomeSlug,
      nursingHomeName,
      countyName || '',
      'waitlist',
      `Added via waitlist form. Timeframe: ${formData.timeframe || 'Not specified'}`
    );
    addedHomes.add(nursingHomeSlug);
    addedHomeNames.push(nursingHomeName);
  }
  
  // Process specific nursing homes from form data
  if (formData.specificNursingHome && formData.specificNursingHome !== 'open_to_suggestions') {
    // Handle multiple selections (comma-separated)
    const selectedHomes = formData.specificNursingHome.split(',').filter(Boolean);
    
    for (const homeSlug of selectedHomes) {
      if (addedHomes.has(homeSlug)) continue;
      
      // Get nursing home details
      const { data: nhData } = await supabase
        .from('nursing_homes')
        .select('center_name, county')
        .eq('nh_slug', homeSlug)
        .limit(1)
        .single();
        
      if (nhData) {
        await addNursingHomeLead(
          userId,
          homeSlug,
          nhData.center_name,
          nhData.county,
          'waitlist',
          `Added via waitlist form. Timeframe: ${formData.timeframe || 'Not specified'}`
        );
        addedHomes.add(homeSlug);
        addedHomeNames.push(nhData.center_name);
      }
    }
  }
  
  // Process preferred locations if no specific homes were selected
  if ((formData.specificNursingHome === 'open_to_suggestions' || !formData.specificNursingHome) && 
      formData.preferredLocation && 
      formData.preferredLocation.length > 0) {
    
    // For each preferred county, add top nursing homes as suggestions
    for (const county of formData.preferredLocation) {
      const countySlug = county.toLowerCase().replace(/\s+/g, '-');
      
      // Find nursing homes in this county
      const { data: countyHomes } = await supabase
        .from('nursing_homes')
        .select('nh_slug, center_name, county')
        .eq('county_slug', countySlug)
        .limit(5);
        
      if (countyHomes && countyHomes.length > 0) {
        // Add suggested homes from this county
        for (const home of countyHomes) {
          if (addedHomes.has(home.nh_slug)) continue;
          
          await addNursingHomeLead(
            userId,
            home.nh_slug,
            home.center_name,
            home.county,
            'suggested',
            `Suggested based on preferred location (${home.county}). Timeframe: ${formData.timeframe || 'Not specified'}`
          );
          
          addedHomes.add(home.nh_slug);
          addedHomeNames.push(home.center_name);
        }
      }
    }
  }
  
  return addedHomeNames;
}

async function addNursingHomeLead(
  userId: string, 
  nursingHomeId: string, 
  nursingHomeName: string, 
  location: string, 
  status: string, 
  notes: string
) {
  // Check if lead already exists
  const { data: existingLead } = await supabase
    .from('nursing_home_leads')
    .select('id')
    .eq('user_id', userId)
    .eq('nursing_home_id', nursingHomeId)
    .limit(1);
    
  if (!existingLead?.length) {
    // Add new lead
    await supabase.from('nursing_home_leads').insert({
      user_id: userId,
      nursing_home_id: nursingHomeId,
      nursing_home_name: nursingHomeName,
      location: location,
      status: status,
      notes: notes
    });
  }
}
